import React, { forwardRef } from "react";

const Input = forwardRef(
  (
    {
      label,
      type = "text",
      name,
      value,
      onChange,
      placeholder,
      error,
      required = false,
      disabled = false,
      helperText,
      className = "",
      id,
      autoComplete,
      maxLength,
      minLength,
      pattern,
      size,
      onBlur,
      onFocus,
      ariaLabel,
      ariaDescribedBy,
      // Add additional props for better mobile experience
      inputMode,
      autoCapitalize,
      // Add icon support
      iconLeft,
      iconRight,
      onIconRightClick,
    },
    ref
  ) => {
    // Generate unique IDs for accessibility
    const inputId =
      id || `input-${name}-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `error-${inputId}`;
    const helperId = `helper-${inputId}`;

    return (
      <div className={`w-full mb-4 ${className}`}>
        {label && (
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor={inputId}
              className="block text-sm font-medium text-text"
            >
              {label}
              {required && <span className="ml-1 text-danger">*</span>}
            </label>

            {helperText && (
              <span id={helperId} className="text-xs text-neutral-dark">
                {helperText}
              </span>
            )}
          </div>
        )}

        <div className="relative">
          {iconLeft && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-neutral-dark">
              {iconLeft}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoComplete={autoComplete}
            maxLength={maxLength}
            minLength={minLength}
            pattern={pattern}
            size={size}
            inputMode={inputMode}
            autoCapitalize={autoCapitalize}
            aria-label={ariaLabel || label}
            aria-invalid={!!error}
            aria-describedby={`${error ? errorId : ""} ${helperText ? helperId : ""} ${ariaDescribedBy || ""}`}
            className={`
            w-full
            px-4 py-2.5
            ${iconLeft ? "pl-10" : ""}
            ${iconRight ? "pr-10" : ""}
            text-base md:text-sm
            border rounded-lg
            shadow-sm
            placeholder-neutral-dark/60
            bg-background
            text-text
            transition duration-200
            focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary
            ${error ? "border-danger" : "border-neutral-dark"} 
            ${disabled ? "bg-neutral-light text-neutral-dark/60 cursor-not-allowed" : "hover:border-primary"}
            ${type === "number" ? "appearance-none" : ""}
          `}
          />

          {iconRight && (
            <div
              className={`absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-dark ${onIconRightClick ? "cursor-pointer hover:text-primary" : "pointer-events-none"}`}
              onClick={onIconRightClick}
            >
              {iconRight}
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} className="mt-1.5 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

// Usage example (commented out for reference)
/*
Example usage:
<Input
  label="Email Address"
  type="email"
  name="email"
  placeholder="your@email.com"
  required
  helperText="We'll never share your email"
  iconLeft={<MailIcon className="w-5 h-5" />}
  iconRight={<InfoIcon className="w-5 h-5" />}
  onIconRightClick={() => alert('More info')}
/>
*/

export default Input;
